import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { apiService } from '../services/apiService';
import { redirectToTenant } from '../utils/tenantResolver';

const SuperAdminTenantManagementPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [redirectingTenantId, setRedirectingTenantId] = useState<number | null>(null);

  const query = useMemo(() => ({ search: search.trim() || undefined }), [search]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getAdminTenants(query);
      setRows(data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [query.search]);

  const loginToTenant = async (tenant: any) => {
    const tenantSlug = tenant?.slug;
    if (!tenantSlug) {
      setError('Missing tenant slug for redirect');
      return;
    }

    setError(null);
    setRedirectingTenantId(tenant.tenantId ?? null);
    try {
      const exchange = await apiService.impersonateAdminTenantUser(tenant.tenantId);
      redirectToTenant(
        exchange.tenantSlug || tenantSlug,
        `/login?returnUrl=${encodeURIComponent('/dashboard')}`,
        exchange.exchangeToken
      );
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to auto-login into tenant');
      setRedirectingTenantId(null);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ApartmentIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Tenant Management
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search tenant"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="outlined" onClick={load} disabled={loading}>
            Reload
          </Button>
        </Box>
      </Paper>

      {loading && <CircularProgress size={22} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Serial No.</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Billing</TableCell>
              <TableCell>Licenses</TableCell>
              <TableCell>Allowed Domain</TableCell>
              <TableCell>Members</TableCell>
              <TableCell>Active Members</TableCell>
              <TableCell>Projects</TableCell>
              <TableCell>Environments</TableCell>
              <TableCell>Collections</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Login</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((t, index) => (
              <TableRow key={t.tenantId}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{t.name || '-'}</TableCell>
                <TableCell>{t.slug || '-'}</TableCell>
                <TableCell>{t.status || '-'}</TableCell>
                <TableCell>{t.planType || '-'}</TableCell>
                <TableCell>{t.billingCycle || '-'}</TableCell>
                <TableCell>{t.licenseCount ?? '-'}</TableCell>
                <TableCell>{t.allowedEmailDomain || '-'}</TableCell>
                <TableCell>{t.memberCount ?? 0}</TableCell>
                <TableCell>{t.activeMemberCount ?? 0}</TableCell>
                <TableCell>{t.projectCount ?? 0}</TableCell>
                <TableCell>{t.environmentCount ?? 0}</TableCell>
                <TableCell>{t.collectionCount ?? 0}</TableCell>
                <TableCell>{t.createdAt || '-'}</TableCell>
                <TableCell>{t.updatedAt || '-'}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => loginToTenant(t)}
                    disabled={!t.slug || redirectingTenantId === t.tenantId}
                  >
                    {redirectingTenantId === t.tenantId ? 'Redirecting...' : 'Login'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SuperAdminTenantManagementPage;

